// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import * as api from '../../api/client'

const STATUS_LABEL: Record<string, string> = {
  created: 'Created',
  label_purchased: 'Label purchased',
  in_transit: 'In transit',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  exception: 'Exception',
  cancelled: 'Cancelled',
}
const STATUS_COLOR: Record<string, string> = {
  created: '#64748b',
  label_purchased: '#6366f1',
  in_transit: '#0ea5e9',
  out_for_delivery: '#06b6d4',
  delivered: '#16a34a',
  exception: '#dc2626',
  cancelled: '#9ca3af',
}
const fmt = (s: string | null) => (s ? new Date(s).toLocaleString() : null)

/**
 * Delivery status + carrier event timeline for one Rx. Read-only; safe for
 * providers (the /rx/{id}/delivery endpoint scopes by their access set).
 */
export default function RxDelivery({ rxId }: { rxId: string }) {
  const [shipment, setShipment] = useState<api.RxDelivery | null>(null)
  const [events, setEvents] = useState<api.ShipmentEvent[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let live = true
    api.getRxDelivery(rxId)
      .then(r => { if (live) { setShipment(r.shipment); setEvents(r.events || []) } })
      .catch(() => { /* no delivery info is a normal state */ })
      .finally(() => { if (live) setLoaded(true) })
    return () => { live = false }
  }, [rxId])

  if (!loaded) return null
  if (!shipment) {
    return (
      <div style={{ marginTop: 16 }}>
        <h2 style={h2}>Delivery</h2>
        <div style={{ ...card, color: 'var(--color-text-mute)', fontSize: 13 }}>No shipment for this prescription yet.</div>
      </div>
    )
  }

  const color = STATUS_COLOR[shipment.status] || '#64748b'
  const label = STATUS_LABEL[shipment.status] || shipment.status

  return (
    <div style={{ marginTop: 16 }}>
      <h2 style={h2}>Delivery</h2>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ ...pill, background: color }}>{label}</span>
          <span style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>
            {shipment.carrier}{shipment.service_level ? ` · ${shipment.service_level}` : ''}
          </span>
          {shipment.tracking_number && (
            <span style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace', color: 'var(--color-text-mute)' }}>
              {shipment.tracking_number}
            </span>
          )}
        </div>

        <div style={{ marginTop: 8, fontSize: 13, color: 'var(--color-text-soft)', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {shipment.carrier_status_text && <span>{shipment.carrier_status_text}</span>}
          {fmt(shipment.shipped_at) && <span>Shipped {fmt(shipment.shipped_at)}</span>}
          {fmt(shipment.delivered_at) && (
            <span>Delivered {fmt(shipment.delivered_at)}{shipment.pod_signature_name ? ` · signed by ${shipment.pod_signature_name}` : ''}</span>
          )}
        </div>

        {events.length > 0 && (
          <div style={{ marginTop: 12, borderTop: '1px solid var(--color-border)', paddingTop: 10 }}>
            {events.map((e, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '5px 0', fontSize: 13 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', marginTop: 4, flexShrink: 0,
                  background: STATUS_COLOR[e.status] || '#94a3b8' }} aria-hidden="true" />
                <div>
                  <div>{e.description || STATUS_LABEL[e.status] || e.status}{e.location ? ` — ${e.location}` : ''}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>{fmt(e.occurred_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const h2: React.CSSProperties = { fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-text-mute)', margin: '0 0 8px' }
const card: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 8, padding: '12px 14px', background: 'var(--color-surface)' }
const pill: React.CSSProperties = { color: '#fff', fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999 }
